// ── SPORTCENTER · NÖRDERI ─────────────────────────────────────────
// Tre kort som svarar på frågor blocket inte kunde svara på alls:
// vad sålde vilken avdelning, finns det en sommaraffär, och vad köper vi in.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useSportcenterProdukter, aggregera, periodSpann, PeriodValjare } from './SportcenterProdukter';
import { tkr, fmtMonth, raStartYear } from '../utils';

const SPORT_COLOR = '#1A3D2B';


const knapp = (aktiv, farg = SPORT_COLOR) => ({
  background: aktiv ? farg : 'transparent',
  color: aktiv ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '3px 10px', fontSize: 11, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});

const kr = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';

// ── AVDELNINGAR ───────────────────────────────────────────────────
// Qlik har inget avdelningsfält för Sportcenter — [Produkt kategori Zettle]
// är produktnamn (skidmodeller, hyrpaket, "Verkstad"). Splitten personalen
// tänker i finns alltså bara som en mappning, och den bor i
// intel_config/sportcenter_avdelningar precis som Brasseriets huvudgrupper.
//
// Ingen kategori gissas åt någon. Osatta hamnar i Ej tilldelat tills en
// människa placerat dem — en felgissad hyresintäkt som tyst räknas som butik
// är värre än en tom rad, för då syns det inte att den är fel.
const AVDELNINGAR = ['Uthyrning', 'Skidor', 'Butik', 'Verkstad', 'Spårkort'];
const EJ_SATT = 'Ej tilldelat';

function AvdelningCard() {
  const produkter = useSportcenterProdukter();
  const [vald, setVald]       = useState('sasong');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [mapping, setMapping] = useState(null);
  const [redigera, setRedigera] = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [sparar, setSparar]   = useState(false);
  const [fel, setFel]         = useState('');

  useEffect(() => {
    getDoc(doc(db, 'intel_config', 'sportcenter_avdelningar'))
      .then((s) => setMapping(s.exists() ? (s.data().mapping || {}) : {}))
      .catch((e) => { console.error('Avdelningar mapping:', e); setMapping({}); });
  }, []);

  const eget  = !!(from && to && from <= to);
  const spann = eget ? { from, to } : periodSpann(vald);

  const per = useMemo(() => {
    const by = {};
    for (const it of aggregera(produkter, spann.from, spann.to)) {
      const a = (mapping || {})[it.name] || EJ_SATT;
      if (!by[a]) by[a] = { avd: a, antal: 0, intakt: 0 };
      by[a].antal  += it.antal  || 0;
      by[a].intakt += it.intakt || 0;
    }
    return by;
  }, [produkter, spann.from, spann.to, mapping]);

  const ordning = [...AVDELNINGAR.filter((a) => per[a]), ...(per[EJ_SATT] ? [EJ_SATT] : [])];
  const totIntakt = ordning.reduce((s, a) => s + per[a].intakt, 0);
  const totAntal  = ordning.reduce((s, a) => s + per[a].antal, 0);

  // Listan man sitter och betar av: hela historiken, oavsett vald period.
  // Kartan ska gå att rita i augusti, och då har ingen vinterkategori sålts
  // på månader.
  const allaKategorier = useMemo(() => {
    const allt = periodSpann('allt');
    return aggregera(produkter, allt.from, allt.to).sort((a, b) => b.intakt - a.intakt);
  }, [produkter]);
  const osatta = allaKategorier.filter((p) => !(mapping || {})[p.name]).length;

  const spara = async () => {
    setSparar(true); setFel('');
    try {
      await setDoc(doc(db, 'intel_config', 'sportcenter_avdelningar'), {
        mapping, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '?',
      });
      setDirty(false);
    } catch (e) {
      console.error('spara avdelningar:', e);
      setFel('Kunde inte spara — saknas skrivregeln för intel_config/sportcenter_avdelningar?');
    } finally { setSparar(false); }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Försäljning per avdelning</div>
        <button onClick={() => setRedigera((r) => !r)} style={knapp(redigera)}>
          ✎ Avdelningar{osatta > 0 ? ` (${osatta})` : ''}
        </button>
      </div>
      {!redigera && <PeriodValjare vald={vald} setVald={setVald} from={from} setFrom={setFrom} to={to} setTo={setTo} />}

      {(!produkter || mapping === null) && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar…</div>
      )}

      {produkter && mapping !== null && !redigera && (
        <div className="period-table">
          <div className="period-header period-row-5">
            <span>Avdelning</span>
            <span style={{ textAlign: 'right' }}>Antal</span>
            <span style={{ textAlign: 'right' }}>Intäkt</span>
            <span style={{ textAlign: 'right' }}>Snittpris</span>
            <span style={{ textAlign: 'right' }}>Andel</span>
          </div>
          {ordning.map((a) => (
            <div key={a} className="period-row period-row-5" style={a === EJ_SATT ? { color: '#854F0B' } : undefined}>
              <span style={{ fontWeight: 600 }}>{a}</span>
              <span style={{ textAlign: 'right' }}>{per[a].antal > 0 ? `${Math.round(per[a].antal)} st` : '–'}</span>
              <span style={{ textAlign: 'right' }}>{kr(per[a].intakt)}</span>
              <span style={{ textAlign: 'right' }}>{per[a].antal > 0 ? kr(per[a].intakt / per[a].antal) : '–'}</span>
              <span style={{ textAlign: 'right' }}>{totIntakt > 0 ? `${Math.round(per[a].intakt / totIntakt * 100)}%` : '–'}</span>
            </div>
          ))}
          {ordning.length > 0 && (
            <div className="period-row period-row-5" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <span>Summa</span>
              <span style={{ textAlign: 'right' }}>{Math.round(totAntal)} st</span>
              <span style={{ textAlign: 'right' }}>{kr(totIntakt)}</span>
              <span style={{ textAlign: 'right' }}>{totAntal > 0 ? kr(totIntakt / totAntal) : '–'}</span>
              <span style={{ textAlign: 'right' }}>100%</span>
            </div>
          )}
          {ordning.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 0', fontFamily: 'system-ui,sans-serif' }}>Ingen försäljning i perioden.</div>
          )}
        </div>
      )}

      {produkter && mapping !== null && redigera && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>
            Tilldela avdelning per kategori, sorterat på intäkt över hela historiken — inte bara vald period,
            så vinterns hyrpaket och skidmodeller går att placera mitt i sommaren. Osatta räknas som {EJ_SATT}.
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' }}>
            {allaKategorier.map((p) => (
              <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--panel2)', fontFamily: 'system-ui,sans-serif' }}>
                <span style={{ fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name} <span style={{ color: 'var(--text-secondary)', fontSize: 10.5 }}>({kr(p.intakt)})</span>
                </span>
                <select
                  value={(mapping || {})[p.name] || ''}
                  onChange={(e) => {
                    setMapping((m) => {
                      const ny = { ...m };
                      if (e.target.value) ny[p.name] = e.target.value; else delete ny[p.name];
                      return ny;
                    });
                    setDirty(true);
                  }}
                  style={{ fontSize: 11.5, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', flexShrink: 0 }}
                >
                  <option value="">— {EJ_SATT}</option>
                  {AVDELNINGAR.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            ))}
            {allaKategorier.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 0', fontFamily: 'system-ui,sans-serif' }}>Inga kategorier sålda senaste 3 månaderna.</div>
            )}
          </div>
          {fel && <div style={{ fontSize: 11.5, color: '#A32D2D', marginTop: 6, fontFamily: 'system-ui,sans-serif' }}>{fel}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={spara} disabled={!dirty || sparar} style={{
              background: dirty ? SPORT_COLOR : 'transparent', color: dirty ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 8, padding: '5px 14px', fontSize: 12,
              cursor: dirty ? 'pointer' : 'default', fontFamily: 'system-ui,sans-serif',
            }}>{sparar ? 'Sparar…' : 'Spara avdelningar'}</button>
          </div>
        </>
      )}

      <div className="note" style={{ marginTop: 10 }}>
        Källa: Qlik · Zettle [Produkt kategori Zettle]. Belopp ex. moms. Snittpris = intäkt ÷ antal sålda artiklar —
        inte snittkvitto: Qlik har inget kvitto-ID på Zettle-raderna, så antal köp går inte att räkna.
        Intäkt utan Zettle-kategori (bl.a. Visbook-hyror) ligger utanför listan.
      </div>
    </div>
  );
}

// ── SOMMARBUTIKEN ─────────────────────────────────────────────────
// Maj–oktober är "stängt" i alla samtal om Sportcenter, men det säljs för
// ett par hundra tusen — och det är löpning, inte skidor. Ingen har följt
// den affären, så ingen har märkt att den tappar.
const SOMMAR_MAN = [5, 6, 7, 8, 9, 10];

function SommarbutikCard({ rows }) {
  const produkter = useSportcenterProdukter();

  const { ar, serie, jmf } = useMemo(() => {
    const val = new Map();
    for (const r of rows || []) {
      const ym = Number(r[0]);
      if (!/^\d{6}$/.test(String(ym))) continue;
      val.set(ym, { rev: r[4] || 0, harData: (r[6] || 0) > 0 });
    }
    // Senaste året som har någon sommarmånad med data
    let senaste = null;
    for (const [ym, v] of val) {
      const y = Math.floor(ym / 100), m = ym % 100;
      if (SOMMAR_MAN.includes(m) && v.harData && (senaste === null || y > senaste)) senaste = y;
    }
    if (senaste === null) return { ar: null, serie: [], jmf: null };

    const s = SOMMAR_MAN.map((m) => {
      const nu  = val.get(senaste * 100 + m);
      const fjol = val.get((senaste - 1) * 100 + m);
      return {
        m,
        label: fmtMonth(`${senaste}${String(m).padStart(2, '0')}`).slice(0, 3),
        nu:   nu?.harData   ? nu.rev   : null,
        fjol: fjol?.harData ? fjol.rev : null,
      };
    });
    // Samma disciplin som säsongskurvan: bara månader båda åren har.
    const j = s.reduce((a, p) => (p.nu == null || p.fjol == null ? a
      : { nu: a.nu + p.nu, fjol: a.fjol + p.fjol, n: a.n + 1 }), { nu: 0, fjol: 0, n: 0 });
    return { ar: senaste, serie: s, jmf: { ...j, pct: j.fjol > 0 ? Math.round((j.nu - j.fjol) / j.fjol * 100) : null } };
  }, [rows]);

  if (ar === null) return null;
  // Vad sommaren säljer — räknat på just sommarmånaderna, inte på "senaste
  // 3 mån", som glider in i skidsäsongen så fort hösten kommer.
  const topp = aggregera(produkter, `${ar}-05-01`, `${ar}-10-31`)
    .sort((a, b) => b.intakt - a.intakt).slice(0, 5);
  const max = Math.max(...serie.map((p) => Math.max(p.nu || 0, p.fjol || 0)), 1);

  return (
    <div className="card">
      <div className="card-title">Sommarbutiken {ar}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginBottom: 12 }}>
        Maj–oktober, när skidaffären står still.
        {jmf?.pct !== null && jmf?.n > 0 && (
          <> Jämförbara månader ({jmf.n} st): {tkr(jmf.nu)} mot {tkr(jmf.fjol)} ifjol
            <span style={{ fontWeight: 700, color: jmf.pct >= 0 ? '#3B6D11' : '#A32D2D' }}> · {jmf.pct >= 0 ? '+' : ''}{jmf.pct}%</span>.
          </>
        )}
      </div>

      <div className="period-table">
        <div className="period-header period-row-4">
          <span>Månad</span>
          <span style={{ textAlign: 'right' }}>{ar}</span>
          <span style={{ textAlign: 'right' }}>{ar - 1}</span>
          <span style={{ textAlign: 'right' }}>Δ%</span>
        </div>
        {serie.map((p) => {
          const d = p.nu != null && p.fjol > 0 ? Math.round((p.nu - p.fjol) / p.fjol * 100) : null;
          return (
            <div key={p.m} className="period-row period-row-4">
              <span>{p.label}</span>
              <span style={{ textAlign: 'right' }}>
                {p.nu == null ? '–' : kr(p.nu)}
                {p.nu != null && (
                  <div style={{ height: 3, marginTop: 3, background: 'var(--panel2)', borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${Math.round(p.nu / max * 100)}%`, background: SPORT_COLOR, borderRadius: 2 }} />
                  </div>
                )}
              </span>
              <span style={{ textAlign: 'right' }}>{p.fjol == null ? '–' : kr(p.fjol)}</span>
              <span style={{ textAlign: 'right', color: d === null ? 'var(--text)' : d >= 0 ? '#3B6D11' : '#A32D2D' }}>
                {d === null ? '–' : `${d >= 0 ? '+' : ''}${d}%`}
              </span>
            </div>
          );
        })}
      </div>

      {topp.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 14, marginBottom: 6, fontFamily: 'system-ui,sans-serif', color: 'var(--text)' }}>
            Vad sommaren säljer (maj–okt {ar})
          </div>
          {topp.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, padding: '2px 0', fontFamily: 'system-ui,sans-serif' }}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{p.name}</span>
              <span style={{ flexShrink: 0, fontWeight: 600 }}>{Math.round(p.antal)} st · {kr(p.intakt)}</span>
            </div>
          ))}
        </>
      )}

      <div className="note" style={{ marginTop: 10 }}>Källa: Qlik — Resultatenhet Sportcenter. Månader utan data visas som –, inte som noll.</div>
    </div>
  );
}

// ── INKÖP ─────────────────────────────────────────────────────────
// Lagervärdet (1xxx) finns inte i portalen: SIE-inläsningen sparar bara
// resultatkonton. Det som FINNS är inköpen (4xxx) — vad som köpts in per
// månad och varugrupp. Admin-låst eftersom intel_bokforing bär hela
// koncernens kontonivå, löner inkluderade.
function InkopCard() {
  const [manader, setManader] = useState(null);
  const [namn, setNamn]       = useState({});
  const [fel, setFel]         = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', 'sc')));
        const ut = [];
        snap.forEach((d) => { const v = d.data(); if (v.ym && v.rader) ut.push(v); });
        ut.sort((a, b) => b.ym.localeCompare(a.ym));
        setManader(ut);
        const meta = await getDoc(doc(db, 'intel_bokforing', 'sc_meta'));
        if (meta.exists()) setNamn(meta.data().konton || {});
      } catch (e) {
        console.error('Inköp:', e);
        setFel('Kunde inte läsa bokföringen.');
        setManader([]);
      }
    })();
  }, []);

  const { perKonto, perManad, total } = useMemo(() => {
    const k = {}, m = {};
    let t = 0;
    const raStart = `${raStartYear()}07`;
    for (const doc0 of manader || []) {
      if (doc0.ym < raStart) continue;
      for (const r of doc0.rader || []) {
        if (!/^4\d{3}$/.test(r.konto)) continue;
        // Kostnader ligger med debettecken (positiva) i SIE-inläsningen.
        k[r.konto] = (k[r.konto] || 0) + r.belopp;
        m[doc0.ym] = (m[doc0.ym] || 0) + r.belopp;
        t += r.belopp;
      }
    }
    return {
      perKonto: Object.entries(k).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])),
      perManad: Object.entries(m).sort((a, b) => b[0].localeCompare(a[0])),
      total: t,
    };
  }, [manader]);

  if (manader === null) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar bokföring…</p></div>;
  }

  return (
    <div className="card">
      <div className="card-title">Inköp per varugrupp · RÅ {String(raStartYear() % 100).padStart(2, '0')}/{String((raStartYear() + 1) % 100).padStart(2, '0')}</div>
      {fel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>{fel}</div>}
      {!fel && perKonto.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
          Ingen bokföring inläst för Bruksvallsliden SC AB det här räkenskapsåret. Ladda upp SIE-filen under Budget → Bokföring.
        </div>
      )}
      {perKonto.length > 0 && (
        <div className="period-table">
          <div className="period-header period-row-4">
            <span>Konto</span>
            <span>Varugrupp</span>
            <span style={{ textAlign: 'right' }}>Inköp</span>
            <span style={{ textAlign: 'right' }}>Andel</span>
          </div>
          {perKonto.map(([konto, belopp]) => (
            <div key={konto} className="period-row period-row-4">
              <span>{konto}</span>
              <span>{namn[konto] || '—'}</span>
              <span style={{ textAlign: 'right' }}>{tkr(belopp)}</span>
              <span style={{ textAlign: 'right' }}>{total !== 0 ? `${Math.round(belopp / total * 100)}%` : '–'}</span>
            </div>
          ))}
          <div className="period-row period-row-4" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <span>Summa</span>
            <span />
            <span style={{ textAlign: 'right' }}>{tkr(total)}</span>
            <span style={{ textAlign: 'right' }}>100%</span>
          </div>
        </div>
      )}
      {perManad.length > 0 && (
        <>
          <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 14, marginBottom: 6, fontFamily: 'system-ui,sans-serif' }}>Per månad</div>
          <div className="period-table">
            {perManad.map(([ym, belopp]) => (
              <div key={ym} className="period-row period-row-4">
                <span>{fmtMonth(ym)}</span>
                <span />
                <span style={{ textAlign: 'right' }}>{tkr(belopp)}</span>
                <span />
              </div>
            ))}
          </div>
        </>
      )}
      <div className="note" style={{ marginTop: 10 }}>
        Källa: SIE-inläsningen (intel_bokforing), konto 4000–4999. Detta är INKÖP, inte lagervärde —
        varulagret är ett balanskonto (1xxx) och SIE-inläsningen sparar bara resultatkonton.
        Kortet syns bara för admin: bokföringen bär hela koncernens kontonivå.
      </div>
    </div>
  );
}

export default function SportcenterNorderi({ rows, isAdmin }) {
  return (
    <>
      <AvdelningCard />
      <SommarbutikCard rows={rows} />
      {isAdmin && <InkopCard />}
    </>
  );
}
